/**
 * Route guard that ensures the Resume Studio consent is on file
 * BEFORE rendering ANY Resume Studio page. Addresses Mark's Phase 2
 * observation: previously the consent modal only fired on the list
 * page — a consent-less user visiting /resume-studio/new directly
 * saw the picker (and hit a 403 on submit). Now every Resume Studio
 * route runs this guard first.
 *
 * 2026-09-23 launch-scope addition: when the backend returns HTTP 503
 * from /resume-studio/status the guard renders an honest
 * "Resume Studio isn't available yet." notice (no fake coming-soon
 * promises, no dates). All RS pages route through this guard, so this
 * single change gates every URL.
 *
 * While consent is being checked we render a small "checking…" line
 * (no full-screen spinner — this call is fast and the UI shouldn't
 * flash). If consent is missing, the consent modal opens; declining
 * it routes back to /dashboard so the user isn't stranded.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { ResumeStudioConsentModal } from "@/components/ResumeStudioConsentModal";

export function RequireResumeStudioConsent({ children }) {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | ok | needs_consent | disabled
  const [showModal, setShowModal] = useState(false);

  const refresh = () => {
    api.get("/resume-studio/status").then((r) => {
      if (r.data?.consent_on_file?.present) {
        setState("ok");
        setShowModal(false);
      } else {
        setState("needs_consent");
        setShowModal(true);
      }
    }).catch((err) => {
      // 503 from the RS router means the feature is disabled at the
      // launch-scope flag layer. Render the honest notice instead of
      // the consent modal (which would be misleading).
      if (err?.response?.status === 503) {
        setState("disabled");
        setShowModal(false);
        return;
      }
      // Any other error — fail closed to the consent flow so the user
      // is never silently trapped on a page that will 403 on submit.
      setState("needs_consent");
      setShowModal(true);
    });
  };
  useEffect(() => { refresh(); }, []);

  if (state === "checking") {
    return (
      <AppShell>
        <p className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary"
           data-testid="resume-studio-consent-guard-loading">
          Checking your Resume Studio consent…
        </p>
      </AppShell>
    );
  }

  if (state === "disabled") {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-16"
             data-testid="resume-studio-disabled-notice">
          <h1 className="text-2xl font-heading font-medium text-fg-primary">
            Resume Studio isn't available yet.
          </h1>
          <p className="mt-4 text-fg-secondary leading-relaxed">
            Everything else in CRA3D — the assessment, three directions,
            and AI-learning pathways — is fully available today.
          </p>
          <div className="mt-8">
            <Link
              to="/dashboard"
              data-testid="resume-studio-disabled-back-link"
              className="inline-flex items-center rounded-md bg-accent-warm px-4 py-2 text-sm font-medium text-white hover:bg-accent-warm/90"
            >
              Back to dashboard
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  if (state === "needs_consent") {
    return (
      <AppShell>
        <ResumeStudioConsentModal
          open={showModal}
          onConsented={refresh}
          onCancel={() => { setShowModal(false); navigate("/dashboard"); }}
        />
        <p className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary"
           data-testid="resume-studio-consent-guard-needed">
          Resume Studio needs your one-time consent first.
        </p>
      </AppShell>
    );
  }

  return children;
}

export default RequireResumeStudioConsent;
